import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Badge,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { ClientManagementState } from "../types";
import type {
  ClientJobRole,
  ClientUser,
  CentralUser,
  CreateSingleUserRequest,
  CreateSingleUserResponse,
  ResetUserPasswordResponse,
  ServiceOverrides,
} from "../api/clientManagement";
import { SettingsSection } from "./SettingsSection";
import { CreateUserDialog } from "./CreateUserDialog";
import { UserEditDialog } from "./UserEditDialog";
import { UserRow } from "./UserRow";
import { buildCsv, downloadCsv } from "../../utils/csv";

interface UserManagementTabProps {
  effectiveClientId: string | null;
  isCentralAdmin: boolean;
  managementState: ClientManagementState;
  setAssignUserId: (value: string) => void;
  assignUserToClientMutation: UseMutationResult<unknown, unknown, string>;
  removeUserFromClientMutation: UseMutationResult<unknown, unknown, string>;
  deleteUserMutation: UseMutationResult<unknown, unknown, string>;
  clientUsersQuery: UseQueryResult<{ data: ClientUser[] }>;
  departmentsList: { id: string; name: string }[];
  jobRolesQuery: UseQueryResult<{ data: ClientJobRole[] }>;
  regionsList: { id: string; name: string }[];
  centralUsersAllQuery: UseQueryResult<{ data: CentralUser[] }>;
  updateUserServicesMutation: UseMutationResult<
    unknown,
    unknown,
    { userId: string; payload: Partial<ServiceOverrides> }
  >;
  resetUserPasswordMutation: UseMutationResult<
    ResetUserPasswordResponse,
    unknown,
    {
      userId: string;
      temporary: boolean;
      sendEmail: boolean;
      password?: string;
      loginUrl?: string;
    }
  >;
  updatingUserServicesId: string | null;
  assignUserToDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { departmentId: string; userId: string }
  >;
  removeUserFromDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { departmentId: string; userId: string }
  >;
  assignUserToJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { jobRoleId: string; userId: string }
  >;
  removeUserFromJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { jobRoleId: string; userId: string }
  >;
  assignUserToRegionMutation: UseMutationResult<
    unknown,
    unknown,
    { regionId: string; userId: string }
  >;
  removeUserFromRegionMutation: UseMutationResult<
    unknown,
    unknown,
    { regionId: string; userId: string }
  >;
  updateUserAdminMutation: UseMutationResult<
    unknown,
    unknown,
    { userId: string; isAdmin: boolean }
  >;
  createUserMutation: UseMutationResult<
    CreateSingleUserResponse,
    unknown,
    { clientId: string; payload: CreateSingleUserRequest }
  >;
}

export const UserManagementTab: React.FC<UserManagementTabProps> = ({
  effectiveClientId,
  isCentralAdmin,
  managementState,
  setAssignUserId,
  assignUserToClientMutation,
  removeUserFromClientMutation,
  deleteUserMutation,
  clientUsersQuery,
  departmentsList,
  jobRolesQuery,
  regionsList,
  centralUsersAllQuery,
  updateUserServicesMutation,
  resetUserPasswordMutation,
  updatingUserServicesId,
  assignUserToDepartmentMutation,
  removeUserFromDepartmentMutation,
  assignUserToJobRoleMutation,
  removeUserFromJobRoleMutation,
  assignUserToRegionMutation,
  removeUserFromRegionMutation,
  updateUserAdminMutation,
  createUserMutation,
}) => {
  const [search, setSearch] = useState("");
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [sort, setSort] = useState<SortOption>("name-asc");
  const [showFilters, setShowFilters] = useState(false);
  const [editingUser, setEditingUser] = useState<ClientUser | null>(null);
  const [selectedAssignUsers, setSelectedAssignUsers] = useState<CentralUser[]>([]);
  const [assigningCount, setAssigningCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState({
    department: "",
    jobRole: "",
    wearJeopardy: "",
    consent: "",
    status: "",
  });

  const activeFilterCount = [
    columnFilters.department,
    columnFilters.jobRole,
    columnFilters.wearJeopardy,
    columnFilters.consent,
    columnFilters.status,
  ].filter(Boolean).length;

  const users = clientUsersQuery.data?.data ?? [];
  const sortedUsers = useSortedUsers(users, search, sort, columnFilters);

  const connectedCount = sortedUsers.filter((u) =>
    u.integrations?.some((i) => i.connected),
  ).length;

  const handleClearFilters = () => {
    setSearch("");
    setColumnFilters({ department: "", jobRole: "", wearJeopardy: "", consent: "", status: "" });
  };

  const handleDownloadUsers = () => {
    if (!isCentralAdmin || !sortedUsers.length) return;

    const csv = buildCsv(
      [
        "name",
        "email",
        "departments",
        "job_roles",
        "connected_integrations",
        "first_connected_at",
        "wear_jeopardy_status",
        "wear_jeopardy_reason",
        "enabled_services",
        "consent_status",
        "requires_consent",
        "status",
      ],
      sortedUsers.map((user) => {
        const fullName = `${user.first_name} ${user.last_name}`.trim();
        const departments = (user.departments ?? [])
          .map((d) => d.name)
          .filter(Boolean)
          .join("; ");
        const jobRoles = (user.job_roles ?? [])
          .map((r) => r.name)
          .filter(Boolean)
          .join("; ");
        const integrations = (user.integrations ?? [])
          .filter((i) => i.connected)
          .map((i) => i.name || i.provider)
          .filter(Boolean)
          .join("; ");
        const enabledServices = Object.entries(user.enabled_services ?? {})
          .filter(([, enabled]) => enabled)
          .map(([key]) => key)
          .join("; ");
        const isConnected = !!user.integrations?.some((i) => i.connected);

        return [
          fullName,
          user.email,
          departments,
          jobRoles,
          integrations,
          user.first_connected_at ?? "",
          user.wear_jeopardy_status ?? "",
          user.wear_jeopardy_status_reason ?? "",
          enabledServices,
          user.consent_status ?? "",
          user.requires_consent == null ? "" : user.requires_consent ? "Yes" : "No",
          isConnected ? "Connected" : "Not connected",
        ];
      }),
    );

    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadCsv(`client-users-${stamp}.csv`, csv);
  };

  const assignableUsers = useMemo(() => {
    if (!centralUsersAllQuery.data?.data) return [];
    if (!effectiveClientId) return [];
    return centralUsersAllQuery.data.data.filter(
      (user) => !user.client?.id || user.client.id === effectiveClientId,
    );
  }, [centralUsersAllQuery.data, effectiveClientId]);

  const handleAssignAll = async () => {
    if (!selectedAssignUsers.length) return;
    setAssigningCount(selectedAssignUsers.length);
    for (const user of selectedAssignUsers) {
      try {
        await assignUserToClientMutation.mutateAsync(user.id);
      } catch {
        // continue with remaining users even if one fails
      }
    }
    setSelectedAssignUsers([]);
    setAssigningCount(0);
  };

  const isAssigning = assigningCount > 0;

  return (
    <SettingsSection>
      {!effectiveClientId && (
        <Alert severity="info">Select a client to manage users.</Alert>
      )}

      {/* ── Assign users ─────────────────────────────────────────────────────── */}
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" fontWeight={600}>
          Assign users to client
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select one or more unassigned users to attach to this client.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="flex-start">
          {isCentralAdmin ? (
            <Autocomplete
              multiple
              options={assignableUsers}
              loading={centralUsersAllQuery.isLoading}
              value={selectedAssignUsers}
              onChange={(_e, value) => setSelectedAssignUsers(value)}
              getOptionLabel={(option) =>
                `${option.first_name} ${option.last_name} (${option.email})`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ minWidth: 360, flex: 1, maxWidth: 560 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Search users"
                  placeholder={selectedAssignUsers.length ? "" : "Name or email…"}
                  helperText="Only unassigned users or users already on this client."
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={`${option.first_name} ${option.last_name}`}
                    size="small"
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
          ) : (
            <TextField
              size="small"
              label="User ID"
              value={managementState.assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
              sx={{ minWidth: 260 }}
            />
          )}
          <Button
            variant="contained"
            disabled={
              !effectiveClientId ||
              isAssigning ||
              (isCentralAdmin ? selectedAssignUsers.length === 0 : !managementState.assignUserId.trim())
            }
            onClick={
              isCentralAdmin
                ? handleAssignAll
                : () => assignUserToClientMutation.mutate(managementState.assignUserId.trim())
            }
            sx={{ flexShrink: 0, alignSelf: "flex-start" }}
          >
            {isAssigning
              ? `Assigning…`
              : selectedAssignUsers.length > 1
                ? `Assign ${selectedAssignUsers.length} users`
                : "Assign"}
          </Button>
        </Stack>
      </Stack>

      <Divider />

      {/* ── User list controls ───────────────────────────────────────────────── */}
      <Stack spacing={1.5}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
          <TextField
            size="small"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 360 }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              label="Sort by"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortOption)}
            >
              <MenuItem value="name-asc">Name (A–Z)</MenuItem>
              <MenuItem value="name-desc">Name (Z–A)</MenuItem>
              <MenuItem value="email-asc">Email (A–Z)</MenuItem>
              <MenuItem value="email-desc">Email (Z–A)</MenuItem>
              <MenuItem value="status-desc">Connected first</MenuItem>
              <MenuItem value="status-asc">Disconnected first</MenuItem>
            </Select>
          </FormControl>

          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant={showFilters ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters((prev) => !prev)}
              size="small"
            >
              Filters
            </Button>
          </Badge>

          {(activeFilterCount > 0 || search) && (
            <Button size="small" variant="text" onClick={handleClearFilters}>
              Clear all
            </Button>
          )}

          {isCentralAdmin && (
            <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCreateUserOpen(true)}
                disabled={!effectiveClientId}
              >
                Create User
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleDownloadUsers}
                disabled={clientUsersQuery.isLoading || sortedUsers.length === 0}
              >
                Export CSV
              </Button>
            </Stack>
          )}
        </Stack>

        {/* ── Advanced filters panel ─────────────────────────────────────────── */}
        <Collapse in={showFilters}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            sx={{
              p: 2,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <TextField
              size="small"
              label="Department"
              placeholder="Filter…"
              value={columnFilters.department}
              onChange={(e) =>
                setColumnFilters((prev) => ({ ...prev, department: e.target.value }))
              }
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              label="Job role"
              placeholder="Filter…"
              value={columnFilters.jobRole}
              onChange={(e) =>
                setColumnFilters((prev) => ({ ...prev, jobRole: e.target.value }))
              }
              sx={{ minWidth: 160 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Wear jeopardy</InputLabel>
              <Select
                label="Wear jeopardy"
                value={columnFilters.wearJeopardy}
                onChange={(e) =>
                  setColumnFilters((prev) => ({
                    ...prev,
                    wearJeopardy: String(e.target.value),
                  }))
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="not_connected">Not connected</MenuItem>
                <MenuItem value="recently_connected">Recently connected</MenuItem>
                <MenuItem value="not_worn">Not worn</MenuItem>
                <MenuItem value="intermittent_wear">Intermittent wear</MenuItem>
                <MenuItem value="wearing_consistently">Wearing consistently</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Consent</InputLabel>
              <Select
                label="Consent"
                value={columnFilters.consent}
                onChange={(e) =>
                  setColumnFilters((prev) => ({
                    ...prev,
                    consent: String(e.target.value),
                  }))
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="required">Required</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="withdrawn">Withdrawn</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Connection</InputLabel>
              <Select
                label="Connection"
                value={columnFilters.status}
                onChange={(e) =>
                  setColumnFilters((prev) => ({
                    ...prev,
                    status: String(e.target.value),
                  }))
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="connected">Connected</MenuItem>
                <MenuItem value="not connected">Not connected</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Collapse>

        {/* ── Stats ─────────────────────────────────────────────────────────── */}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            label={`${sortedUsers.length} user${sortedUsers.length !== 1 ? "s" : ""}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`${connectedCount} connected`}
            size="small"
            color={connectedCount > 0 ? "success" : "default"}
            variant="outlined"
          />
          {activeFilterCount > 0 && (
            <Chip
              label={`${activeFilterCount} filter${activeFilterCount !== 1 ? "s" : ""} active`}
              size="small"
              color="primary"
              variant="filled"
              onDelete={handleClearFilters}
            />
          )}
        </Stack>
      </Stack>

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      {clientUsersQuery.isLoading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading users…</Typography>
        </Stack>
      ) : sortedUsers.length ? (
        <TableContainer sx={{ width: "100%" }}>
          <Table size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Departments</TableCell>
                <TableCell>Job roles</TableCell>
                <TableCell>Services</TableCell>
                <TableCell>First connected</TableCell>
                <TableCell>Wear jeopardy</TableCell>
                <TableCell>Consent</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => {
                const isConnected = !!user.integrations?.some((i) => i.connected);
                return (
                  <UserRow
                    key={user.id}
                    user={user}
                    statusLabel={isConnected ? "Connected" : "Not connected"}
                    statusTone={isConnected ? "success" : "muted"}
                    onOpenEdit={() => setEditingUser(user)}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No users match your filters.
        </Typography>
      )}

      <UserEditDialog
        open={!!editingUser}
        user={editingUser}
        onClose={() => setEditingUser(null)}
        isCentralAdmin={isCentralAdmin}
        departments={departmentsList}
        jobRoles={jobRolesQuery.data?.data ?? []}
        regions={regionsList}
        updateUserServicesMutation={updateUserServicesMutation}
        resetUserPasswordMutation={resetUserPasswordMutation}
        updatingUserServicesId={updatingUserServicesId}
        assignUserToDepartmentMutation={assignUserToDepartmentMutation}
        removeUserFromDepartmentMutation={removeUserFromDepartmentMutation}
        assignUserToJobRoleMutation={assignUserToJobRoleMutation}
        removeUserFromJobRoleMutation={removeUserFromJobRoleMutation}
        assignUserToRegionMutation={assignUserToRegionMutation}
        removeUserFromRegionMutation={removeUserFromRegionMutation}
        removeUserFromClientMutation={removeUserFromClientMutation}
        deleteUserMutation={deleteUserMutation}
        updateUserAdminMutation={updateUserAdminMutation}
      />

      {effectiveClientId && (
        <CreateUserDialog
          open={createUserOpen}
          clientId={effectiveClientId}
          departments={departmentsList}
          jobRoles={jobRolesQuery.data?.data ?? []}
          regions={regionsList}
          createUserMutation={createUserMutation}
          onClose={() => {
            setCreateUserOpen(false);
            clientUsersQuery.refetch();
          }}
        />
      )}
    </SettingsSection>
  );
};

// ── Types & utilities ──────────────────────────────────────────────────────────

type SortOption =
  | "name-asc"
  | "name-desc"
  | "email-asc"
  | "email-desc"
  | "status-asc"
  | "status-desc";

const normalize = (value: string) => value.toLowerCase().trim();

const useSortedUsers = (
  users: ClientUser[],
  search: string,
  sort: SortOption,
  columnFilters: {
    department: string;
    jobRole: string;
    wearJeopardy: string;
    consent: string;
    status: string;
  },
) => {
  return useMemo(() => {
    const filtered = users.filter((user) => {
      const needle = normalize(search);
      const name = `${user.first_name} ${user.last_name}`.toLowerCase();
      const departments = (user.departments ?? [])
        .map((d) => d.name.toLowerCase())
        .join(" ");
      const jobRoles = (user.job_roles ?? [])
        .map((r) => r.name.toLowerCase())
        .join(" ");
      const isConnected = user.integrations?.some((i) => i.connected)
        ? "connected"
        : "not connected";

      if (
        needle &&
        !name.includes(needle) &&
        !user.email.toLowerCase().includes(needle)
      ) {
        return false;
      }
      if (
        columnFilters.department &&
        !departments.includes(normalize(columnFilters.department))
      ) {
        return false;
      }
      if (
        columnFilters.jobRole &&
        !jobRoles.includes(normalize(columnFilters.jobRole))
      ) {
        return false;
      }
      if (
        columnFilters.wearJeopardy &&
        (user.wear_jeopardy_status ?? "") !== columnFilters.wearJeopardy
      ) {
        return false;
      }
      if (
        columnFilters.consent &&
        (user.consent_status ?? "") !== columnFilters.consent
      ) {
        return false;
      }
      if (columnFilters.status && isConnected !== columnFilters.status) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
      const emailA = a.email.toLowerCase();
      const emailB = b.email.toLowerCase();
      const statusA = a.integrations?.some((i) => i.connected) ? 1 : 0;
      const statusB = b.integrations?.some((i) => i.connected) ? 1 : 0;

      switch (sort) {
        case "name-desc":
          return nameB.localeCompare(nameA);
        case "email-asc":
          return emailA.localeCompare(emailB);
        case "email-desc":
          return emailB.localeCompare(emailA);
        case "status-asc":
          return statusA - statusB;
        case "status-desc":
          return statusB - statusA;
        case "name-asc":
        default:
          return nameA.localeCompare(nameB);
      }
    });
  }, [columnFilters, users, search, sort]);
};
